import React from 'react';
import { Box, Button, CircularProgress, Divider, FormControlLabel, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { GridColumnsPanel, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarDensitySelector } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { EmptyContent } from '../../../../components/empty-content';
import { Iconify } from '../../../../components';
import { gridToolbarColumnsButtonStyle } from '../../../../styles/NewLeadTableRowStyle';
import { PATH_DASHBOARD } from '../../../../routes/paths';

const TableLoadingOverlay = () => (
    <Box
        sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 10,
        }}
    >
        <CircularProgress size={40} />
    </Box>
);

const NAME_FIELDS = ['lead_name', 'first_name', 'last_name'];
const getTogglableColumns = (columns) => columns.filter(col => !NAME_FIELDS.includes(col.field)).map(col => col.field);

// Defined at module level so the reference is stable — DataGrid won't remount the panel
// on parent re-renders. Custom props flow in via slotProps.columnsPanel (not closures).
const CustomColumnsPanel = ({ onResetColumns, onToggleCombineName, isCombinedName, isEnforcedByPractice, onManageTemplates, ...props }) => ( // eslint-disable-line react/prop-types
    <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 280,
        maxHeight: 480,
        '& .MuiDataGrid-panelHeader': { display: 'none' },
        '& .MuiDataGrid-panelContent': { maxHeight: 320, overflow: 'auto' },
        '& .MuiDataGrid-columnsPanelRow': { px: 1 },
    }}>
        {isEnforcedByPractice && (
            <Box sx={{ py: 1, px: 2, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                <Iconify icon='solar:lock-bold' width={14} sx={{ color: 'text.disabled' }} />
                <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Managed by practice admin
                </Typography>
            </Box>
        )}
        {(onToggleCombineName || isEnforcedByPractice) && (
            <Box sx={{ py: 1.25, px: 2, flexShrink: 0 }}>
                <Tooltip title={isEnforcedByPractice ? 'Managed by practice admin' : ''}>
                    <FormControlLabel
                        control={
                            <Switch
                                size='small'
                                checked={isCombinedName || false}
                                onChange={(e) => onToggleCombineName?.(e.target.checked)}
                                disabled={isEnforcedByPractice}
                            />
                        }
                        label='Combine name'
                        sx={{
                            mx: 0, display: 'flex',
                            '& .MuiFormControlLabel-label': { fontSize: '13px', fontWeight: 500, color: isEnforcedByPractice ? 'text.disabled' : 'text.primary' }
                        }}
                    />
                </Tooltip>
            </Box>
        )}
        <Divider />
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', ...(isEnforcedByPractice && { pointerEvents: 'none', opacity: 0.5 }) }}>
            <GridColumnsPanel {...props} getTogglableColumns={getTogglableColumns} />
        </Box>
        {onResetColumns && (
            <>
                <Divider />
                <Box sx={{ py: 0.75, px: 1.5, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        size='small'
                        startIcon={<Iconify icon='solar:restart-bold' width={14} height={14} />}
                        onClick={onResetColumns}
                        sx={{
                            color: 'text.secondary', textTransform: 'none', fontWeight: 500,
                            fontSize: '12px', whiteSpace: 'nowrap', minWidth: 'auto',
                            '& .MuiButton-startIcon': { mr: 0.5 },
                        }}
                    >
                        Reset
                    </Button>
                </Box>
            </>
        )}
        {onManageTemplates && (
            <>
                <Divider />
                <Box sx={{ py: 0.75, px: 1.5, flexShrink: 0 }}>
                    <Button
                        size='small'
                        endIcon={<Iconify icon='eva:arrow-forward-fill' width={13} height={13} />}
                        onClick={onManageTemplates}
                        sx={{
                            color: 'text.secondary', textTransform: 'none', fontWeight: 400,
                            fontSize: '11.5px', whiteSpace: 'nowrap', minWidth: 'auto', p: 0,
                            '&:hover': { color: 'primary.main', bgcolor: 'transparent' },
                            '& .MuiButton-endIcon': { ml: 0.4 },
                        }}
                    >
                        Manage checklist templates
                    </Button>
                </Box>
            </>
        )}
    </Box>
);

const NoResultsOverlay = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No results found</Typography>
    </Box>
);

// GridToolbarInner — reads Redux directly so its parent's useCallback is dependency-free.
// This is what keeps the `toolbar` slot reference stable regardless of selection state.
function GridToolbarInner({ isMultiTreatmentTable }) { // eslint-disable-line react/prop-types
    if (isMultiTreatmentTable) return null;
    return (
        <>
            <GridToolbarContainer>
                <Stack spacing={1} flexGrow={1} direction='row' alignItems='center' justifyContent='flex-end'>
                    <GridToolbarDensitySelector
                        sx={gridToolbarColumnsButtonStyle}
                    />
                    <GridToolbarColumnsButton
                        startIcon={<Iconify icon={'system-uicons:settings'} width={24} height={24} />}
                        sx={gridToolbarColumnsButtonStyle}
                    />
                </Stack>
            </GridToolbarContainer>
        </>
    );
}

// All slot references are stable module-level components — DataGrid identity-checks
// never see a new reference, so the columns panel is never unmounted when Redux state
// or parent props change.
const STABLE_SLOTS = {
    loadingOverlay: TableLoadingOverlay,
    columnsPanel: CustomColumnsPanel,
    noResultsOverlay: NoResultsOverlay,
    toolbar: GridToolbarInner,
};

const DataGridSlots = ({ isMultiTreatmentTable, tableTitle, practiceId, hasActiveFilters, onClearFilters, onResetColumns, onToggleCombineName, isCombinedName, isEnforcedByPractice }) => {
    const navigate = useNavigate();
    const handleManageTemplates = React.useCallback(() => {
        if (!practiceId) return;
        const base = PATH_DASHBOARD.task.tasks.templates(practiceId);
        const url = tableTitle ? `${base}?table=${encodeURIComponent(tableTitle)}` : base;
        navigate(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, tableTitle]);

    const noRowsOverlay = React.useCallback(() => hasActiveFilters ? (
        <EmptyContent
            title='No results found'
            description='Try adjusting or clearing your filters'
            action={
                onClearFilters ? (
                    <Button
                        variant='outlined'
                        color='error'
                        size='small'
                        startIcon={<Iconify icon='solar:trash-bin-trash-bold' />}
                        onClick={onClearFilters}
                        sx={{ mt: 2 }}
                    >
                        Clear all filters
                    </Button>
                ) : null
            }
        />
    ) : (
        <EmptyContent title='No Data' />
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ), [hasActiveFilters, onClearFilters]);

    const slots = React.useMemo(() => ({
        ...STABLE_SLOTS,
        noRowsOverlay,
    }), [noRowsOverlay]);

    const slotProps = React.useMemo(() => ({
        toolbar: {
            isMultiTreatmentTable,
            tableTitle,
        },
        columnsPanel: {
            onResetColumns: isEnforcedByPractice ? null : onResetColumns,
            onToggleCombineName: isEnforcedByPractice ? null : onToggleCombineName,
            isCombinedName,
            isEnforcedByPractice,
            onManageTemplates: practiceId ? handleManageTemplates : null,
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [isMultiTreatmentTable, tableTitle, isEnforcedByPractice, onResetColumns, onToggleCombineName, isCombinedName, practiceId, handleManageTemplates]);

    return { slots, slotProps };
};
export default DataGridSlots;
